import React, { useState, useEffect } from 'react';
import { AlertCircle, X, CheckCircle, Settings, Gift, Users, Palette, Calendar, UserPlus, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { companyService } from '../../services/companyService';
import { useRewards } from '../../hooks/useRewards';
import { Link } from 'react-router-dom';

const CompanySetupTips = () => {
  const { user } = useAuth();
  const { rewards } = useRewards(user?.companyId);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const loadCompanySettings = async () => {
      if (user?.companyId) {
        try {
          const company = await companyService.getById(user.companyId);
          setCompanySettings(company);
        } catch (error) {
          console.error('Erro ao carregar configurações da empresa:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadCompanySettings();
  }, [user?.companyId]);

  // Verificar quais configurações estão pendentes
  const getPendingTasks = () => {
    if (!companySettings) return [];

    const tasks = [];

    // 1. Dados da empresa (Nome, descrição e logo)
    const needsCompanyData = !companySettings.name || 
                            !companySettings.description || 
                            !companySettings.logo;
    
    if (needsCompanyData) {
      tasks.push({
        id: 'company-data',
        title: 'Dados da Empresa',
        description: 'Configure nome, descrição e logo da sua empresa',
        icon: Building2,
        link: '/company/settings',
        color: 'blue'
      });
    }

    // 2. Configuração de pontos (pontos por real, bônus de boas-vindas, resgate mínimo)
    const needsPointsConfig = !companySettings.pointsPerReal || 
                             companySettings.pointsPerReal === 0 ||
                             companySettings.welcomeBonus === undefined ||
                             !companySettings.minimumRedemption || 
                             companySettings.minimumRedemption === 0;

    if (needsPointsConfig) {
      tasks.push({
        id: 'points-config',
        title: 'Configuração de Pontos',
        description: 'Defina pontos por real, bônus de boas-vindas e resgate mínimo',
        icon: Settings,
        link: '/company/settings',
        color: 'green'
      });
    }

    // 3. Configurações de expiração de pontos
    if (!companySettings.pointsExpiration?.enabled) {
      tasks.push({
        id: 'points-expiration',
        title: 'Configurar Expiração de Pontos',
        description: 'Defina se e quando os pontos dos clientes devem expirar',
        icon: Calendar,
        link: '/company/settings',
        color: 'orange'
      });
    }

    // 4. Ativar/Desativar o sistema de indicação
    if (!companySettings.referralSettings?.enabled) {
      tasks.push({
        id: 'referral-settings',
        title: 'Sistema de Indicação',
        description: 'Configure pontos por indicação para aumentar sua base de clientes',
        icon: UserPlus,
        link: '/company/settings',
        color: 'indigo'
      });
    }

    // 5. Cadastrar ao menos um prêmio
    if (!rewards || rewards.length === 0) {
      tasks.push({
        id: 'rewards',
        title: 'Cadastrar Prêmios',
        description: 'Crie ao menos um prêmio como desconto mínimo por pontos',
        icon: Gift,
        link: '/company/rewards',
        color: 'purple'
      });
    }

    return tasks;
  };

  const pendingTasks = getPendingTasks();
  const completedTasks = Math.max(0, 5 - pendingTasks.length);
  const progressPercentage = (completedTasks / 5) * 100;

  if (loading || dismissed || pendingTasks.length === 0) {
    return null;
  }

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800'
    };
    return colors[color] || colors.blue;
  };

  const getIconColor = (color) => {
    const colors = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      pink: 'text-pink-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertCircle className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Complete a configuração do seu sistema de fidelidade
            </h3>
            
            {/* Barra de Progresso */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>{completedTasks} de 5 configurações concluídas</span>
                <span>{Math.round(progressPercentage)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>

            <p className="text-gray-700 mb-4">
              Siga estas etapas na ordem para configurar completamente seu sistema de fidelidade:
            </p>

            {/* Lista de Tarefas Pendentes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pendingTasks.slice(0, 4).map((task, index) => {
                const IconComponent = task.icon;
                return (
                  <Link
                    key={task.id}
                    to={task.link}
                    className={`flex items-center p-3 rounded-lg border transition-colors hover:bg-opacity-80 ${getColorClasses(task.color)}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full bg-white ${getIconColor(task.color)} text-xs font-bold`}>
                        {index + 1}
                      </div>
                      <IconComponent className={`h-5 w-5 ${getIconColor(task.color)}`} />
                    </div>
                    <div className="flex-1 ml-3">
                      <div className="font-medium text-sm">{task.title}</div>
                      <div className="text-xs opacity-75">{task.description}</div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {pendingTasks.length > 4 && (
              <div className="mt-3 text-sm text-gray-600">
                + {pendingTasks.length - 4} etapa{pendingTasks.length - 4 > 1 ? 's' : ''} restante{pendingTasks.length - 4 > 1 ? 's' : ''}
              </div>
            )}

            <div className="mt-4 flex items-center justify-center text-sm">
              <button
                onClick={() => setDismissed(true)}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Lembrar depois
              </button>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default CompanySetupTips;
